import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatSaturday, formatTimeRange, DAYS_OF_WEEK } from "../lib/fmt";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import MembersTab from "../components/admin/MembersTab";
import CalendarTab from "../components/admin/CalendarTab";
import BroadcastSmsDialog from "../components/admin/BroadcastSmsDialog";
import {
  CalendarDays, Plus, Pencil, Trash2, Download, AlertTriangle, Users,
  TrendingUp, MapPin, Clock, RefreshCw, Loader2, MessageSquare,
} from "lucide-react";

function StatCard({ label, value, sub, icon: Icon, accent = "blue" }) {
  const colors = {
    blue: "bg-brand-blue/10 text-brand-blue",
    green: "bg-brand-green/15 text-brand-green-dark",
    amber: "bg-amber-100 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="card-elevated rounded-lg p-5">
      <div className="flex items-start justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        {Icon && <span className={`p-1.5 rounded ${colors[accent]}`}><Icon className="w-4 h-4" /></span>}
      </div>
      <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [s, sh] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/shifts", { params: { from: new Date().toISOString().slice(0, 10) } }),
      ]);
      setStats(s.data);
      setShifts(sh.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      // Generate next 4 Saturdays
      const today = new Date();
      const dates = [];
      let d = new Date(today);
      const day = d.getDay();
      const diff = (6 - day + 7) % 7 || 7;
      d.setDate(d.getDate() + diff);
      for (let i = 0; i < 4; i++) {
        dates.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 7);
      }
      const { data } = await api.post("/admin/shifts/bulk-generate", { dates });
      toast.success(`Generated ${data.created} new shift${data.created === 1 ? "" : "s"} (${data.skipped} already existed).`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const byDate = {};
  (shifts || []).forEach((s) => { (byDate[s.date] ||= []).push(s); });
  const days = Object.keys(byDate).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats ? (
          <>
            <StatCard label="Upcoming shifts" value={stats.upcoming_shifts} icon={CalendarDays} accent="blue" />
            <StatCard label="Fill rate" value={`${stats.fill_rate}%`} sub={`${stats.filled_slots}/${stats.total_slots} slots`} icon={TrendingUp} accent="green" />
            <StatCard label="Open slots" value={stats.open_slots} icon={Users} accent="slate" />
            <StatCard label="Understaffed" value={stats.understaffed_shifts} sub="shifts under target" icon={AlertTriangle} accent="amber" />
          </>
        ) : (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold text-slate-900">Upcoming Saturdays</h2>
        <Button data-testid="generate-shifts-button" onClick={generate} disabled={generating} className="bg-brand-blue hover:bg-brand-blue-dark">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Generate next 4 Saturdays
        </Button>
      </div>

      {!shifts && <Skeleton className="h-32 rounded-xl" />}

      {shifts && days.length === 0 && (
        <div data-testid="no-shifts-msg" className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <p className="text-slate-600">No upcoming shifts. Generate some Saturdays to get started.</p>
        </div>
      )}

      {days.map((d) => (
        <section key={d} data-testid={`overview-day-${d}`} className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-slate-800">{formatSaturday(d)}</h3>
            <a
              href={`${API_BASE}/admin/export/week?saturday=${d}`}
              data-testid={`export-week-${d}`}
              className="inline-flex items-center text-sm font-medium text-brand-blue hover:text-brand-blue-dark"
            >
              <Download className="w-4 h-4 mr-1" /> Export week
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {byDate[d].map((s) => {
              const isUnderstaffed = s.open > 0;
              return (
                <Link key={s.id} to={`/admin/shifts/${s.id}`} data-testid={`shift-link-${s.id}`} className="card-elevated rounded-lg p-4 block">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-heading font-semibold text-slate-900">{s.route?.name}</h4>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isUnderstaffed ? "bg-amber-100 text-amber-700" : "bg-brand-green/15 text-brand-green-dark"}`}>
                      {s.filled}/{s.total_slots}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {s.start_time}–{s.end_time}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> <span className="truncate">{s.pickup_location}</span></div>
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function RoutesTab() {
  const empty = {
    name: "", pickup_location: "", default_start_time: "09:00", default_end_time: "12:00",
    default_role_slots: { delivery_driver: 1, delivery_coordinator: 1, pickup_driver: 1 },
    description: "", active: true,
  };
  const [routes, setRoutes] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [originalActive, setOriginalActive] = useState(true);
  const [originalShiftCount, setOriginalShiftCount] = useState(0);
  const [purgeOnDeactivate, setPurgeOnDeactivate] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/admin/routes"); setRoutes(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(empty);
    setOriginalActive(true);
    setOriginalShiftCount(0);
    setPurgeOnDeactivate(true);
    setEditing("__new__");
  };
  const openEdit = (r) => {
    setForm({
      ...r,
      default_role_slots: r.default_role_slots || { delivery_driver: 1, delivery_coordinator: 1, pickup_driver: 1 },
    });
    setOriginalActive(r.active !== false);
    setOriginalShiftCount(r.upcoming_shift_count || 0);
    setPurgeOnDeactivate(true);
    setEditing(r.id);
  };

  const save = async () => {
    if (!form.name.trim() || !form.pickup_location.trim()) return toast.error("Name and pickup location are required.");
    try {
      const payload = {
        ...form,
        default_role_slots: {
          delivery_driver: Number(form.default_role_slots.delivery_driver || 0),
          delivery_coordinator: Number(form.default_role_slots.delivery_coordinator || 0),
          pickup_driver: Number(form.default_role_slots.pickup_driver || 0),
        },
      };
      if (editing === "__new__") {
        await api.post("/admin/routes", payload);
        toast.success("Route created.");
      } else {
        const becomingInactive = originalActive && !form.active;
        const willPurge = becomingInactive && purgeOnDeactivate && originalShiftCount > 0;
        const url = willPurge
          ? `/admin/routes/${editing}?purge_upcoming=1`
          : `/admin/routes/${editing}`;
        const { data } = await api.put(url, payload);
        const purged = data?.purged_upcoming_shifts || 0;
        if (purged > 0) {
          toast.success(`Route updated. Cleared ${purged} upcoming shift${purged === 1 ? "" : "s"}.`);
        } else {
          toast.success("Route updated.");
        }
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (r) => {
    const upcoming = r.upcoming_shift_count || 0;
    const hasHistory = true; // we always warn about historical shifts too, backend will report actual counts
    const msg = upcoming > 0
      ? `Delete route "${r.name}"?\n\nThis will also delete ${upcoming} upcoming shift${upcoming === 1 ? "" : "s"} + their signups AND all historical shifts + signups for this route. This cannot be undone.\n\nProceed?`
      : `Delete route "${r.name}"?\n\nThis will also delete every past shift and signup tied to this route. This cannot be undone.\n\nProceed?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/admin/routes/${r.id}?cascade=1`);
      toast.success(`Route deleted. Cleared ${data.deleted_shifts} shift${data.deleted_shifts === 1 ? "" : "s"} + ${data.deleted_signups} signup${data.deleted_signups === 1 ? "" : "s"}.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const purgeFuture = async (r) => {
    if (!window.confirm(`Delete ALL upcoming shifts (and their signups) for "${r.name}"? Past shifts are kept. This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/admin/routes/${r.id}/purge-future-shifts`);
      toast.success(`Cleared ${data.deleted_shifts} upcoming shift${data.deleted_shifts === 1 ? "" : "s"} for ${r.name}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-slate-900">Route templates</h2>
        <Button data-testid="new-route-button" onClick={openNew} className="bg-brand-blue hover:bg-brand-blue-dark"><Plus className="w-4 h-4 mr-1.5" /> New route</Button>
      </div>

      {!routes && <Skeleton className="h-40 rounded-xl" />}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(routes || []).map((r) => (
          <div key={r.id} data-testid={`route-card-${r.id}`} className="card-elevated rounded-lg p-5 flex flex-col gap-3">
            <div>
              <div className="flex items-start justify-between">
                <h3 className="font-heading font-semibold text-slate-900">{r.name}</h3>
                {!r.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Inactive</span>}
              </div>
              {r.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.description}</p>}
            </div>
            <div className="text-xs text-slate-600 space-y-1">
              <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-slate-400" /> <span className="truncate">{r.pickup_location}</span></div>
              <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-400" /> {r.default_start_time}–{r.default_end_time}</div>
              <div className="flex items-center gap-1.5">
                <Users className="w-3 h-3 text-slate-400" />
                {Number(r.default_role_slots?.delivery_driver || 0) + Number(r.default_role_slots?.delivery_coordinator || 0) + Number(r.default_role_slots?.pickup_driver || 0)} positions per Saturday
              </div>
              <div className="flex items-center gap-1.5" data-testid={`route-upcoming-count-${r.id}`}>
                <CalendarDays className="w-3 h-3 text-slate-400" />
                <span className={r.upcoming_shift_count > 0 ? "font-semibold text-slate-700" : "text-slate-500"}>
                  {r.upcoming_shift_count || 0} upcoming shift{(r.upcoming_shift_count || 0) === 1 ? "" : "s"} generated
                </span>
              </div>
            </div>
            <div className="flex gap-2 pt-2 mt-auto border-t border-slate-100 flex-wrap">
              <Button size="sm" variant="ghost" data-testid={`edit-route-${r.id}`} onClick={() => openEdit(r)} className="text-slate-600 hover:text-brand-blue"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
              <Button size="sm" variant="ghost" data-testid={`purge-route-${r.id}`} onClick={() => purgeFuture(r)} className="text-amber-700 hover:text-amber-800"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Clear shifts</Button>
              <Button size="sm" variant="ghost" data-testid={`delete-route-${r.id}`} onClick={() => del(r)} className="text-slate-500 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="route-edit-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing === "__new__" ? "New route" : "Edit route"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Name</Label>
              <Input data-testid="route-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="North Warehouse Run" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Pickup location</Label>
              <Input data-testid="route-pickup-input" value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} placeholder="2210 N Industrial Blvd" className="mt-1.5" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm font-semibold">Start</Label>
                <Input type="time" data-testid="route-start-input" value={form.default_start_time} onChange={(e) => setForm({ ...form, default_start_time: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">End</Label>
                <Input type="time" data-testid="route-end-input" value={form.default_end_time} onChange={(e) => setForm({ ...form, default_end_time: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Total positions</Label>
                <Input type="number" disabled value={Number(form.default_role_slots?.delivery_driver || 0) + Number(form.default_role_slots?.delivery_coordinator || 0) + Number(form.default_role_slots?.pickup_driver || 0)} className="mt-1.5 bg-slate-100" />
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
              <Label className="text-sm font-semibold mb-2 block">Default positions per Saturday</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "delivery_driver", label: "Delivery Driver" },
                  { key: "delivery_coordinator", label: "Coordinator" },
                  { key: "pickup_driver", label: "Pick Up Driver" },
                ].map((r) => (
                  <div key={r.key}>
                    <div className="text-[11px] font-semibold text-slate-600 mb-1">{r.label}</div>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      data-testid={`route-slots-${r.key}`}
                      value={form.default_role_slots?.[r.key] ?? 0}
                      onChange={(e) => setForm({
                        ...form,
                        default_role_slots: { ...form.default_role_slots, [r.key]: e.target.value },
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold">Description (optional)</Label>
              <Textarea data-testid="route-description-input" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold">Active</p>
                <p className="text-xs text-slate-500">Inactive routes won&apos;t be included when generating Saturdays.</p>
              </div>
              <Switch data-testid="route-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            {editing !== "__new__" && originalActive && !form.active && originalShiftCount > 0 && (
              <label
                data-testid="route-purge-on-deactivate"
                className="flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 p-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  data-testid="route-purge-on-deactivate-cb"
                  checked={purgeOnDeactivate}
                  onChange={(e) => setPurgeOnDeactivate(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amber-600"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Also delete the {originalShiftCount} upcoming shift{originalShiftCount === 1 ? "" : "s"} for this route
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Recommended — otherwise old shifts will keep showing on the public board even though the route is off. Past shifts &amp; their signups are always kept.
                  </p>
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="save-route-button" onClick={save} className="bg-brand-blue hover:bg-brand-blue-dark">Save route</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setSettings(data)); }, []);
  if (!settings) return <Skeleton className="h-64 rounded-xl" />;
  const update = (patch) => setSettings({ ...settings, ...patch });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-5 max-w-2xl">
      <h2 className="font-heading text-xl font-semibold text-slate-900">Access, reminders & alerts</h2>
      <p className="text-sm text-slate-500">Manage the volunteer invite code and the timing for reminders. Reminders go out once email/SMS is connected.</p>
      <IntegrationsStatusCard />

      <div className="card-elevated rounded-lg p-5 space-y-4">
        <div>
          <Label className="text-sm font-semibold flex items-center gap-1.5">Volunteer invite code</Label>
          <Input
            data-testid="invite-code-input"
            value={settings.invite_code || ""}
            onChange={(e) => update({ invite_code: e.target.value })}
            className="mt-1.5 uppercase tracking-wider font-mono"
            placeholder="HOPE2026"
          />
          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            Share this code in your newsletter or at board meetings. New volunteers must enter it to register at <span className="font-mono text-slate-700">/register</span>. Rotate any time to invalidate the previous code.
          </p>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <Label className="text-sm font-semibold">Reminder day (before Saturday)</Label>
          <Select value={String(settings.reminder_day_of_week)} onValueChange={(v) => update({ reminder_day_of_week: Number(v) })}>
            <SelectTrigger data-testid="reminder-day-select" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DAYS_OF_WEEK.map((d) => (
                <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold">Reminder hour (ET)</Label>
            <Input data-testid="reminder-hour-input" type="number" min={0} max={23} value={settings.reminder_hour} onChange={(e) => update({ reminder_hour: Number(e.target.value) })} className="mt-1.5" />
            <p className="text-xs text-slate-500 mt-1">Eastern Time · 24-hour (e.g. 7 = 7 AM, 15 = 3 PM). DST handled automatically.</p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Alert organizer if under-filled (days before)</Label>
            <Input data-testid="alert-days-input" type="number" min={1} max={7} value={settings.organizer_alert_days_before} onChange={(e) => update({ organizer_alert_days_before: Number(e.target.value) })} className="mt-1.5" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
          <div>
            <p className="text-sm font-semibold">Email notifications</p>
            <p className="text-xs text-slate-500">Toggle on once email is connected (Resend).</p>
          </div>
          <Switch data-testid="email-enabled-switch" checked={settings.email_enabled} onCheckedChange={(v) => update({ email_enabled: v })} />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
          <div>
            <p className="text-sm font-semibold">SMS notifications</p>
            <p className="text-xs text-slate-500">Toggle on once Twilio is connected.</p>
          </div>
          <Switch data-testid="sms-enabled-switch" checked={!!settings.sms_enabled} onCheckedChange={(v) => update({ sms_enabled: v })} />
        </div>
        <div>
          <Label className="text-sm font-semibold">Organization name</Label>
          <Input data-testid="org-name-input" value={settings.org_name} onChange={(e) => update({ org_name: e.target.value })} className="mt-1.5" />
        </div>

        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-brand-blue hover:bg-brand-blue-dark">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>

      <div className="card-elevated rounded-lg p-5 space-y-2">
        <h3 className="font-heading font-semibold text-slate-900">QA / UAT plan</h3>
        <p className="text-sm text-slate-500">
          A 35-test-case Word document covering every feature in this build. Download, walk through the steps,
          and mark Pass / Fail / Blocked. Re-shareable with your team.
        </p>
        <a
          href="/HopeToHome_QA_Plan.docx"
          download="HopeToHome_QA_Plan.docx"
          data-testid="download-qa-plan"
          className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-brand-blue hover:text-brand-blue-dark"
        >
          ↓ Download QA plan (.docx, 49 KB)
        </a>
      </div>

      <ChangePasswordCard />
      <DangerZoneCard />
    </div>
  );
}

function IntegrationsStatusCard() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(null);
  useEffect(() => {
    api.get("/admin/integrations/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  if (!status) return null;
  const email = status.email;
  const runNow = async (kind) => {
    setSending(kind);
    try {
      const { data } = await api.post(`/admin/reminders/run-now?kind=${kind}`);
      toast.success(`Sent ${data.sent} ${kind}${data.sent === 1 ? "" : "s"} for ${data.date}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(null); }
  };
  return (
    <div
      data-testid="integrations-status-card"
      className={`rounded-lg border p-4 text-sm space-y-2 ${email.enabled ? "border-brand-green/40 bg-brand-green/5" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-heading font-semibold text-slate-900">Email notifications</span>
        <span
          data-testid="email-status-badge"
          className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded ${email.enabled ? "bg-brand-green text-white" : "bg-slate-200 text-slate-600"}`}
        >
          {email.enabled ? "Armed" : "Disabled"}
        </span>
      </div>
      {email.enabled ? (
        <p className="text-xs text-slate-600">
          Sending from <span className="font-mono text-slate-800">{email.sender}</span>.
        </p>
      ) : (
        <p className="text-xs text-slate-500">
          Add <span className="font-mono">RESEND_API_KEY</span> to the backend .env to enable confirmations.
        </p>
      )}
      {email.note && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          {email.note}
        </p>
      )}
      {email.enabled && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            data-testid="run-reminder-now"
            onClick={() => runNow("reminder")}
            disabled={sending !== null}
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {sending === "reminder" ? "Sending…" : "Send reminders now (test)"}
          </button>
          <button
            type="button"
            data-testid="run-alert-now"
            onClick={() => runNow("alert")}
            disabled={sending !== null}
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {sending === "alert" ? "Sending…" : "Send under-fill alert (test)"}
          </button>
        </div>
      )}
    </div>
  );
}


function DangerZoneCard() {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [open, setOpen] = useState(false);

  const wipe = async () => {
    if (confirmText !== "WIPE") return toast.error("Type WIPE exactly to confirm.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shifts/purge-all-upcoming");
      toast.success(`Deleted ${data.deleted_shifts} shift${data.deleted_shifts === 1 ? "" : "s"} and ${data.deleted_signups} signup${data.deleted_signups === 1 ? "" : "s"}.`);
      setOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border-2 border-rose-200 bg-rose-50/50 p-5 space-y-3" data-testid="danger-zone-card">
      <h3 className="font-heading font-semibold text-rose-900 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> Danger zone
      </h3>
      <p className="text-sm text-rose-900/80 leading-relaxed">
        Wipe every upcoming shift and all volunteer signups across every route. Past shifts, past signups, routes, and members stay untouched. Useful when you&apos;re migrating fresh data or starting a new season.
      </p>
      {!open ? (
        <Button
          data-testid="wipe-open-button"
          onClick={() => setOpen(true)}
          variant="outline"
          className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
        >
          <Trash2 className="w-4 h-4 mr-1.5" /> Wipe all upcoming shifts
        </Button>
      ) : (
        <div className="rounded-lg border border-rose-300 bg-white p-4 space-y-3">
          <p className="text-sm font-semibold text-rose-900">
            Type <span className="font-mono bg-rose-100 px-1.5 py-0.5 rounded">WIPE</span> to confirm.
          </p>
          <Input
            data-testid="wipe-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="WIPE"
            className="font-mono"
          />
          <div className="flex gap-2">
            <Button
              data-testid="wipe-cancel-button"
              variant="ghost"
              onClick={() => { setOpen(false); setConfirmText(""); }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              data-testid="wipe-confirm-button"
              onClick={wipe}
              disabled={busy || confirmText !== "WIPE"}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy ? "Wiping…" : "Yes, wipe everything upcoming"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("New password and confirmation don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update password.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="card-elevated rounded-lg p-5 space-y-3" data-testid="change-password-card">
      <h3 className="font-heading font-semibold text-slate-900">Change my password</h3>
      <p className="text-sm text-slate-500">
        Update your own account password — works for any logged-in user (admin or volunteer). No email reset needed.
      </p>
      <div>
        <Label className="text-sm font-semibold">Current password</Label>
        <Input
          type="password"
          data-testid="cp-current"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          className="mt-1.5"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold">New password</Label>
          <Input
            type="password"
            data-testid="cp-new"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            minLength={8}
            className="mt-1.5"
          />
          <p className="text-xs text-slate-400 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <Label className="text-sm font-semibold">Confirm new password</Label>
          <Input
            type="password"
            data-testid="cp-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            className="mt-1.5"
          />
        </div>
      </div>
      <Button type="submit" data-testid="cp-submit" disabled={busy} className="bg-brand-blue hover:bg-brand-blue-dark">
        {busy ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [upcomingSaturdays, setUpcomingSaturdays] = useState([]);

  useEffect(() => {
    if (!checking && !user) navigate("/login");
  }, [checking, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get(`/admin/calendar/saturdays?year=${new Date().getFullYear()}`).then(({ data }) => {
      const today = new Date().toISOString().slice(0, 10);
      setUpcomingSaturdays(
        (data.saturdays || [])
          .filter((s) => s.state === "generated" && s.date >= today)
          .slice(0, 12)
          .map((s) => s.date),
      );
    }).catch(() => {});
  }, [user]);

  if (checking || !user) {
    return (
      <div className="min-h-screen surface-dashboard">
        <Header variant="admin" />
        <div className="max-w-6xl mx-auto px-4 py-10"><Skeleton className="h-40 rounded-xl" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen surface-dashboard">
      <Header variant="admin" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">Organizer dashboard</h1>
            <p className="text-slate-500 text-sm mt-1">Manage Saturday volunteer coverage at a glance.</p>
          </div>
          <Button
            data-testid="open-broadcast-sms-button"
            onClick={() => setBroadcastOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
          >
            <MessageSquare className="w-4 h-4 mr-1.5" /> Send urgent SMS
          </Button>
        </div>
        <Tabs defaultValue="overview" className="space-y-5">
          <TabsList data-testid="admin-tabs" className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="routes" data-testid="tab-routes">Routes</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><OverviewTab /></TabsContent>
          <TabsContent value="calendar"><CalendarTab /></TabsContent>
          <TabsContent value="routes"><RoutesTab /></TabsContent>
          <TabsContent value="members"><MembersTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
      <BroadcastSmsDialog
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
        upcomingSaturdays={upcomingSaturdays}
      />
    </div>
  );
}
